import React, { useState, useContext } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Container,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Chip,
  Button,
} from '@mui/material';
import { useSettings } from '../contexts/SettingsContext';
import { useActivity } from '../contexts/ActivityContext';
import {
  NetworkPing,
  Dns,
  LocationOn,
  Security,
  Language,
  Speed,
  DeviceHub,
  Link,
  VpnKey,
  Search,
  FindInPage,
  SmartToy,
  Code,
  AccountTree,
} from '@mui/icons-material';

import IPScanner from './IPScanner';
import DNSLookup from './DNSLookup';
import GeoLocation from './GeoLocation';
import PortScanner from './PortScanner';
import WhoisLookup from './WhoisLookup';
import SSLChecker from './SSLChecker';
import SubdomainFinder from './SubdomainFinder';
import SpeedTest from './SpeedTest';
import MACLookup from './MACLookup';
import URLTools from './URLTools';
import HashGenerator from './HashGenerator';
import PingSweeper from './tools/PingSweeper';
import TracerouteVisualizer from './tools/TracerouteVisualizer';
import AIAssistant from './AIAssistant';
import NetworkMapper from './tools/NetworkMapper';
import SecurityAuditor from './tools/SecurityAuditor';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tool-tabpanel-${index}`}
      aria-labelledby={`tool-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Dashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const { updateActivity } = useActivity();
  const { settings } = useSettings();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Log tool switch activity
    const toolName = tools[newValue]?.name;
    if (toolName) {
      updateActivity(toolName, 'Tool accessed', { tabIndex: newValue });
    }
  };

  const tools = [
    {
      id: 'security-auditor',
      name: 'Security Auditor',
      description: 'AI-powered vulnerability scanning and security recommendations',
      icon: <Security sx={{ fontSize: 40 }} />,
      component: <SecurityAuditor />,
      color: '#f44336',
    },
    {
      id: 'network-mapper',
      name: 'Network Mapper',
      description: 'Visualize and analyze network topology',
      icon: <AccountTree sx={{ fontSize: 40 }} />,
      component: <NetworkMapper />,
      color: '#00bcd4',
    },
    {
      id: 0,
      name: 'Traceroute',
      description: 'Visualize network path to a host',
      icon: <NetworkPing sx={{ fontSize: 40 }} />,
      component: <TracerouteVisualizer />,
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      name: 'Ping Sweeper',
      description: 'Scan IP ranges for active hosts',
      icon: <NetworkPing sx={{ fontSize: 40 }} />,
      component: <PingSweeper />,
      color: theme.palette.info.main,
    },
    {
      id: 1,
      name: 'IP Scanner',
      description: 'Ping and analyze IP addresses',
      icon: <NetworkPing sx={{ fontSize: 40 }} />,
      component: <IPScanner />,
      color: theme.palette.info.main,
    },
    {
      id: 2,
      name: 'IP Scanner',
      description: 'Ping and analyze IP addresses',
      icon: <NetworkPing sx={{ fontSize: 40 }} />,
      component: <IPScanner />,
      color: theme.palette.primary.main,
    },
    {
      id: 2,
      name: 'DNS Lookup',
      description: 'Resolve domain names to IP addresses',
      icon: <Dns sx={{ fontSize: 40 }} />,
      component: <DNSLookup />,
      color: theme.palette.success.main,
    },
    {
      id: 3,
      name: 'Geo Location',
      description: 'Get geographic information from IP addresses',
      icon: <LocationOn sx={{ fontSize: 40 }} />,
      component: <GeoLocation />,
      color: theme.palette.warning.main,
    },
    {
      id: 4,
      name: 'Port Scanner',
      description: 'Scan for open ports on target hosts',
      icon: <Search sx={{ fontSize: 40 }} />,
      component: <PortScanner />,
      color: theme.palette.error.main,
    },
    {
      id: 5,
      name: 'WHOIS Lookup',
      description: 'Get domain registration and ownership info',
      icon: <FindInPage sx={{ fontSize: 40 }} />,
      component: <WhoisLookup />,
      color: theme.palette.info.main,
    },
    {
      id: 6,
      name: 'SSL Checker',
      description: 'Analyze SSL/TLS certificates',
      icon: <Security sx={{ fontSize: 40 }} />,
      component: <SSLChecker />,
      color: '#4CAF50',
    },
    {
      id: 7,
      name: 'Subdomain Finder',
      description: 'Discover subdomains of a target domain',
      icon: <Language sx={{ fontSize: 40 }} />,
      component: <SubdomainFinder />,
      color: '#FF9800',
    },
    {
      id: 8,
      name: 'Speed Test',
      description: 'Test internet connection speed',
      icon: <Speed sx={{ fontSize: 40 }} />,
      component: <SpeedTest />,
      color: '#9C27B0',
    },
    {
      id: 9,
      name: 'MAC Lookup',
      description: 'Identify device manufacturers',
      icon: <DeviceHub sx={{ fontSize: 40 }} />,
      component: <MACLookup />,
      color: '#607D8B',
    },
    {
      id: 10,
      name: 'URL Tools',
      description: 'Shorten URLs or expand them',
      icon: <Link sx={{ fontSize: 40 }} />,
      component: <URLTools />,
      color: '#795548',
    },
    {
      id: 11,
      name: 'Hash Generator',
      description: 'Generate MD5, SHA1, SHA256 hashes',
      icon: <VpnKey sx={{ fontSize: 40 }} />,
      component: <HashGenerator />,
      color: '#E91E63',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top right, rgba(255, 45, 85, 0.1) 0%, transparent 30%),' +
                  'radial-gradient(circle at bottom left, rgba(0, 229, 255, 0.1) 0%, transparent 30%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23ff2d55\' fill-opacity=\'0.05\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
          opacity: 0.5,
          zIndex: -1,
        },
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5, color: 'text.primary' }}>
        {/* Header */}
        <Box sx={{ 
          mb: 6,
          textAlign: 'center',
          '&::after': {
            content: '""',
            display: 'block',
            width: '80px',
            height: '4px',
            background: 'linear-gradient(90deg, #ff2d55, #00e5ff)',
            margin: '16px auto 0',
            borderRadius: '2px',
          },
        }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(90deg, #ff2d55, #00e5ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              mb: 1,
            }}
          >
            Network Tools
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ 
            maxWidth: '700px',
            mx: 'auto',
            lineHeight: 1.6,
          }}>
            Powerful network utilities at your fingertips. Select a tool below to get started with your network diagnostics and security analysis.
          </Typography>
        </Box>

        {/* AI Assistant Card - Only show if enabled in settings */}
        {settings.enableAI && (
          <Box sx={{ mb: 4 }}>
            <Card 
              sx={{ 
                borderRadius: 2, 
                overflow: 'hidden',
                border: `2px solid ${theme.palette.primary.main}`,
                boxShadow: `0 0 15px ${alpha(theme.palette.primary.main, 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 100%)`,
                  opacity: 1,
                  transition: 'opacity 0.3s ease',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      bgcolor: theme.palette.primary.main, 
                      color: theme.palette.primary.contrastText,
                      p: 1,
                      borderRadius: 1,
                      display: 'flex',
                      mr: 2
                    }}>
                      <SmartToy />
                    </Box>
                    <Typography variant="h5" component="h2" color="text.primary">
                      AI Network Assistant
                    </Typography>
                  </Box>
                  <Chip 
                    label="Beta" 
                    color="primary" 
                    size="small" 
                    variant="outlined"
                    sx={{ fontWeight: 'bold' }} 
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <AIAssistant />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tool Overview Cards */}
        <Grid container spacing={3}>
          {tools.map((tool) => {
            const isActive = activeTab === tool.id;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tool.id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: isActive 
                      ? `2px solid ${tool.color}` 
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    background: isActive
                      ? `rgba(18, 18, 24, 0.8)`
                      : 'rgba(18, 18, 24, 0.6)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 28px ${alpha(tool.color, 0.2)}`,
                      borderColor: tool.color,
                      background: 'rgba(18, 18, 24, 0.8)',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${alpha(tool.color, 0.1)} 0%, transparent 100%)`,
                      opacity: isActive ? 1 : 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover::before': {
                      opacity: 1,
                    },
                  }}
                  onClick={() => setActiveTab(tool.id)}
                >
                  <CardContent sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}>
                    <Box
                      sx={{
                        width: '64px',
                        height: '64px',
                        margin: '0 auto 16px',
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(tool.color, 0.15)} 0%, transparent 100%)`,
                        border: `1px solid ${alpha(tool.color, 0.2)}`,
                        color: tool.color,
                        transition: 'all 0.3s ease',
                        '& svg': {
                          fontSize: '32px',
                        },
                      }}
                    >
                      {tool.icon}
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: 1, 
                        color: 'text.primary',
                        background: isActive 
                          ? `linear-gradient(90deg, ${tool.color}, ${theme.palette.secondary.main})`
                          : 'none',
                        WebkitBackgroundClip: isActive ? 'text' : 'initial',
                        WebkitTextFillColor: isActive ? 'transparent' : 'inherit',
                        display: 'inline-block',
                      }}
                    >
                      {tool.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {tool.description}
                    </Typography>
                    {isActive && (
                      <Box 
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(90deg, ${tool.color}, ${theme.palette.secondary.main})`,
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Active Tool Content */}
        <Card 
          sx={{ 
            mt: 4, 
            background: 'rgba(18, 18, 24, 0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {tools.map((tool) => (
              <TabPanel key={tool.id} value={activeTab} index={tool.id}>
                <Box sx={{ p: 3 }}>
                  {tool.component}
                </Box>
              </TabPanel>
            ))}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default Dashboard;
